import React, { useEffect, useRef } from "react";
import { createChart, ColorType } from "lightweight-charts";

const DetailedTradeView = ({ trade, onClose, darkMode }) => {
  const chartContainerRef = useRef();

  const colors = {
    background: darkMode ? "#141C29" : "#ffffff",
    text: darkMode ? "#ffffff" : "#000000",
    grid: darkMode ? "#2e2e2e" : "#d0d0d0",
    upColor: darkMode ? "#26a69a" : "#4caf50",
    downColor: darkMode ? "#ef5350" : "#f44336",
    volumeUp: darkMode ? "#26a69a80" : "#4caf5080",
    volumeDown: darkMode ? "#ef535080" : "#f4433680",
    stopColor: darkMode ? "#FF9800" : "#FF9800",
    profitColor: darkMode ? "#089981" : "#089981",
  };

  useEffect(() => {
    if (trade.priceData && trade.priceData.prices && chartContainerRef.current) {
      const chart = createChart(chartContainerRef.current, {
        width: chartContainerRef.current.clientWidth,
        height: 400,
        layout: {
          background: { type: ColorType.Solid, color: colors.background },
          textColor: colors.text,
        },
        grid: {
          vertLines: { color: colors.grid },
          horzLines: { color: colors.grid },
        },
        rightPriceScale: {
          borderColor: colors.grid,
        },
        timeScale: {
          timeVisible: true,
          secondsVisible: false,
          borderColor: colors.grid,
          tickMarkFormatter: (time) => {
            const date = new Date(time * 1000);
            return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
          },
        },
      });

      const candlestickSeries = chart.addCandlestickSeries({
        upColor: colors.upColor,
        downColor: colors.downColor,
        borderUpColor: colors.upColor,
        borderDownColor: colors.downColor,
        wickUpColor: colors.upColor,
        wickDownColor: colors.downColor,
      });

      const volumeSeries = chart.addHistogramSeries({
        color: colors.volumeUp,
        priceFormat: {
          type: "volume",
        },
        priceScaleId: "",
      });

      const data = trade.priceData.prices
        .map((price) => {
          const open = price.openPrice ? (price.openPrice.bid + price.openPrice.ask) / 2 : null;
          const high = price.highPrice ? Math.max(price.highPrice.ask, price.highPrice.bid) : null;
          const low = price.lowPrice ? Math.min(price.lowPrice.ask, price.lowPrice.bid) : null;
          const close = price.closePrice ? (price.closePrice.bid + price.closePrice.ask) / 2 : null;

          const timestamp = Math.floor(new Date(price.snapshotTimeUTC).getTime() / 1000);

          return {
            time: timestamp,
            open: open,
            high: high || open,
            low: low || open,
            close: close || open,
          };
        })
        .filter((candle) => candle.open !== null);

      data.sort((a, b) => a.time - b.time);

      const volumeData = trade.priceData.prices.map((price) => {
        const timestamp = Math.floor(new Date(price.snapshotTimeUTC).getTime() / 1000);

        return {
          time: timestamp,
          value: price.lastTradedVolume || 0,
          color: (price.closePrice?.bid || 0) > (price.openPrice?.bid || 0) ? colors.volumeUp : colors.volumeDown,
        };
      });

      volumeData.sort((a, b) => a.time - b.time);

      candlestickSeries.setData(data);
      volumeSeries.setData(volumeData);

      // Add profit distance line
      if (trade.profitDistance) {
        const profitPrice = trade.direction === 'BUY' ? 
          trade.price + trade.profitDistance : 
          trade.price - trade.profitDistance;
        
        const profitLine = chart.addLineSeries({
          color: colors.profitColor,
          lineWidth: 2,
          lineStyle: 1, // Solid line
          title: 'Profit Distance',
        });
        profitLine.setData([
          { time: data[0].time, value: profitPrice },
          { time: data[data.length - 1].time, value: profitPrice }
        ]);
      }

      // Add stop distance line
      if (trade.stopDistance) {
        const stopPrice = trade.direction === 'BUY' ? 
          trade.price - trade.stopDistance : 
          trade.price + trade.stopDistance;
        
        const stopLine = chart.addLineSeries({
          color: colors.stopColor,
          lineWidth: 2,
          lineStyle: 1, // Solid line
          title: 'Stop Distance',
        });
        stopLine.setData([
          { time: data[0].time, value: stopPrice },
          { time: data[data.length - 1].time, value: stopPrice }
        ]);
      }

      // Add marker for position placement
      const tradeTimestamp = Math.floor(new Date(trade.timestamp).getTime() / 1000);
      
      candlestickSeries.setMarkers([
        {
          time: tradeTimestamp,
          position: trade.direction === 'BUY' ? 'belowBar' : 'aboveBar',
          color: trade.direction === 'BUY' ? colors.upColor : colors.downColor,
          shape: trade.direction === 'BUY' ? 'arrowUp' : 'arrowDown',
          text: `${trade.direction} ${trade.size}`,
        },
      ]);

      const handleResize = () => {
        chart.applyOptions({ width: chartContainerRef.current.clientWidth });
      };

      window.addEventListener("resize", handleResize);

      return () => {
        window.removeEventListener("resize", handleResize);
        chart.remove();
      };
    }
  }, [trade, colors]);

  return (
    <div className={`${darkMode ? "text-white" : "text-black"}`}>
      <h2 className="text-2xl font-bold mb-4">{trade.epic} Trade Details</h2>
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <p><strong>Direction:</strong> {trade.direction}</p>
          <p><strong>Type:</strong> {trade.type}</p>
          <p><strong>Size:</strong> {trade.size || "N/A"}</p>
          <p><strong>Profit Distance:</strong> {trade.profitDistance?.toFixed(2) || "N/A"}</p>
          <p><strong>Stop Distance:</strong> {trade.stopDistance?.toFixed(2) || "N/A"}</p>
        </div>
        <div>
          <p><strong>Date:</strong> {new Date(trade.timestamp).toLocaleString()}</p>
          <p><strong>Deal Reference:</strong> {trade.response?.dealReference || "N/A"}</p>
          <p><strong>Price:</strong> {trade.price || "N/A"}</p>
          <p><strong>Profit Level:</strong> {(trade.direction === 'BUY' ? trade.price + trade.profitDistance : trade.price - trade.profitDistance)?.toFixed(2) || "N/A"}</p>
          <p><strong>Stop Level:</strong> {(trade.direction === 'BUY' ? trade.price - trade.stopDistance : trade.price + trade.stopDistance)?.toFixed(2) || "N/A"}</p>
        </div>
      </div>
      <div ref={chartContainerRef} className="w-full h-96 mb-4"></div>
    </div>
  );
};

export default DetailedTradeView;